var fs = require("fs");
var URL = require("mini-url");
var QS = require("mini-querystring");
var qFlat = require("q-flat");
var EventEmitter = require("events-light");
var HttpAgent = require("agentkeepalive");
var HttpsAgent = HttpAgent.HttpsAgent;
var keepalive = { http: new HttpAgent(), https: new HttpsAgent() };

module.exports = function fetcherMiddleware(config) {
  // Middleware defaults.
  config = config || {};
  config.name = config.name || "fetch";
  config.base = config.base || "/";
  config.agent = "agent" in config ? config.agent : keepalive;
  config.forwardIP = "forwardIP" in config ? config.forwardIP : true;
  config.forwardHost = "forwardHost" in config ? config.forwardHost : true;
  config.withCredentials =
    "withCredentials" in config ? config.withCredentials : true;

  return (ctx, next) => {
    // Attach fetch utility to context.
    ctx[config.name] = request;

    // Ensure base path starts with http(s) for node-fetch.
    var base = URL.parse(config.base, ctx.req.origin).href;

    // Create an event emitter for the request.
    var emitter = new EventEmitter();
    for (var field in EventEmitter.prototype) {
      if (typeof EventEmitter.prototype[field] === "function") {
        request[field] = EventEmitter.prototype[field].bind(emitter);
      }
    }

    // Run middleware then cleanup emitter.
    return next().then(cleanup, cleanup);

    // Removes any registered event listeners and throws a provided error.
    function cleanup(err) {
      request.removeAllListeners();
      if (err) throw err;
    }

    // Wrapper arround native fetch.
    function request(path, opts) {
      var form = null;
      var key;
      var jsonField;
      var flatFiles;
      var body;

      // Fetch defaults
      path = path || "/";
      opts = opts || {};
      opts.headers = toHeaders(opts.headers);

      // Forward some proxy headers if we are server side.
      if (!process.browser) {
        // Forward current ip address.
        if (config.forwardIP && !opts.headers.get("X-Forwarded-For")) {
          opts.headers.set("X-Forwarded-For", ctx.req.ip);
        }

        // Forward current host with request.
        if (config.forwardHost && !opts.headers.get("X-Forwarded-Host")) {
          opts.headers.set("X-Forwarded-Host", ctx.req.host);
        }
      } else {
        opts.headers.set("MSB-Web-URL", window.location.href);
      }

      // Automatically send credentials if `withCredentials` is enabled.
      if (config.withCredentials && !opts.credentials) {
        opts.credentials = "same-origin";
        if (!process.browser) {
          var cookie = ctx.req.get("Cookie");
          if (cookie) opts.headers.set("Cookie", cookie);
        }
      }

      // Allow event handlers to modify request options.
      request.emit("request", path, opts);

      // Set base url and parse path.
      var parsed = URL.parse(path, base);

      // Append query string.
      if (typeof opts.query === "object") {
        parsed = {
          protocol: parsed.protocol,
          host: parsed.host,
          pathname: parsed.pathname,
          search: "?" + QS.stringify(cast(qFlat(opts.query)), false),
          hash: parsed.hash,
        };
        parsed.href = URL.stringify(parsed);
      }

      // Flatten files to bracket-notation leaf field names so nested file slots
      // (e.g. branding's customBranding[logoImg]) become one binary part each,
      // instead of being stringified to "[object Object]". Flat top-level files
      // (avatar, videoFile, thumbnail, …) are returned unchanged.
      flatFiles = flattenFileFields(opts.files);

      // Full JSON metadata + file parts (scheduling uploads): one form field holds the
      // same object shape as application/json POST — avoids qFlat turning arrays into
      // strings or `windows[0][…]` keys. Upstream parses `data` (or opts.multipartJsonField).
      if (typeof opts.multipartJsonBody === "string" && opts.files) {
        form = form || new FormData();
        jsonField = opts.multipartJsonField || "data";
        form.append(jsonField, opts.multipartJsonBody);
        appendFileFields(form, flatFiles);
      } else if (typeof opts.body === "object" && opts.body !== null) {
        form = form || new FormData();
        body = cast(qFlat(opts.body));
        // Drop the exact leaf keys uploaded as binary parts so they aren't also
        // sent as text. Branding nests files (customBranding[logoImg]); remove
        // only the leaf, never the parent object (that wiped the whole section).
        for (key in flatFiles) {
          delete body[key];
        }
        for (key in body) {
          if (body[key] === undefined) continue;
          form.append(key, body[key]);
        }
      }

      // Append file data. Do NOT use qFlat here — formidable File objects are
      // plain objects; qFlat turns them into thumbnail[path], thumbnail[_writeStream], …
      // and breaks binary upload to the API. One leaf field → one file part.
      if (
        Object.keys(flatFiles).length > 0 &&
        typeof opts.multipartJsonBody !== "string"
      ) {
        form = form || new FormData();
        appendFileFields(form, flatFiles);
      }

      // Attach form if created.
      if (form) opts.body = form;

      // Setup default agent for node-fetch.
      // Default to config agent / keepalive with the ability to set a custom
      // agent for http and https.
      if (!opts.agent && config.agent)
        opts.agent = config.agent[parsed.protocol.slice(0, -1)];

      // Call native fetch.
      return fetch(parsed.href, opts).then((res) => {
        // Allow event handlers to respond to a response.
        request.emit("response", parsed.href, res);
        return res;
      });
    }
  };
};

/**
 * Turns an object into request headers.
 */
function toHeaders(obj) {
  var headers = new Headers();
  if (typeof obj !== "object") return headers;
  for (var key in obj) headers.set(key, obj[key]);
  return headers;
}

/**
 * Ignores empty strings and undefined.request
 * Converts date to iso string.
 * Converts everything else to a string.
 */
function cast(data) {
  var result = {};

  for (var key in data) {
    var val = data[key];
    if (val === "" || val === undefined) continue;
    else if (val instanceof Date && isFinite(val))
      result[key] = val.toISOString();
    else result[key] = String(val);
  }

  return result;
}

/**
 * A file leaf to stop recursing on when flattening nested file slots. Two shapes:
 * - browser File/Blob (client-side form submit) — a Blob instance with no `.path`;
 * - server formidable file (SSR) — a plain object carrying a temp `.path` on disk.
 * @param {unknown} val
 */
function isFileLike(val) {
  if (val == null || typeof val !== "object") return false;
  if (typeof Blob !== "undefined" && val instanceof Blob) return true;
  return typeof val.path === "string" && val.path !== "";
}

/**
 * Flatten nested file containers into a single-level map keyed by bracket-notation
 * leaf field names, e.g. { customBranding: { logoImg: File } } -> { "customBranding[logoImg]": File }.
 * Flat top-level files (e.g. { avatar: File }) and arrays of files are returned unchanged,
 * so non-branding upload flows behave exactly as before.
 * @param {Record<string, unknown> | null | undefined} files
 * @param {string} [prefix]
 * @param {Record<string, unknown>} [out]
 * @returns {Record<string, unknown>}
 */
function flattenFileFields(files, prefix, out) {
  out = out || {};
  if (!files || typeof files !== "object") return out;
  for (var key in files) {
    if (!Object.hasOwn(files, key)) continue;
    var val = files[key];
    if (val === undefined || val === null) continue;
    var fieldName = prefix ? prefix + "[" + key + "]" : key;
    if (Array.isArray(val) || isFileLike(val)) {
      out[fieldName] = val;
    } else if (typeof val === "object") {
      flattenFileFields(val, fieldName, out);
    } else {
      out[fieldName] = val;
    }
  }
  return out;
}

function appendFileFields(form, files) {
  for (var key in files) {
    if (!Object.hasOwn(files, key)) continue;
    var val = files[key];
    if (val === undefined) continue;
    if (Array.isArray(val)) {
      for (var i = 0; i < val.length; i++) {
        if (val[i] === undefined) continue;
        appendOneFilePart(form, key, val[i]);
      }
    } else {
      appendOneFilePart(form, key, val);
    }
  }
}

function appendOneFilePart(form, key, val) {
  if (
    !process.browser &&
    val &&
    typeof val === "object" &&
    typeof val.path === "string" &&
    val.path
  ) {
    var buf = fs.readFileSync(val.path);
    var blob = new Blob([buf], {
      type: val.type || "application/octet-stream",
    });
    form.append(key, blob, val.name || val.filename || "upload");
    return;
  }
  form.append(key, val);
}
